import { type FC } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEmailCampaignOptionsQuery } from "@/partner/entities/marketing";
import {
  selectedCampaigns,
  type EmailCampaignOption as Option,
} from "../model/emailCampaignChips";

interface EmailCampaignsAutocompleteProps {
  label: string;
  value: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

/**
 * Рассылки, автоматизации и цепочки платформы (до 100, R9) для фильтров
 * аналитики и условий аудитории. Лежит в feature-ui, а не в entities:
 * shared/components/Filter по правилам boundaries импортирует только
 * feature-ui (как LinksAutocomplete).
 */
export const EmailCampaignsAutocomplete: FC<
  EmailCampaignsAutocompleteProps
> = ({ label, value, onChange, disabled, error, helperText }) => {
  const { t } = useTranslation();
  const { data, loading } = useEmailCampaignOptionsQuery({
    fetchPolicy: "cache-first",
  });
  const options: Option[] = (data?.automations.nodes ?? []).map((node) => ({
    id: node.id,
    name: node.name,
  }));
  const selected = selectedCampaigns(
    value,
    options,
    loading,
    t("shared.filter.unknownEmailCampaign")
  );

  return (
    <Autocomplete<Option, true>
      multiple
      fullWidth
      loading={loading}
      disabled={disabled}
      options={options}
      value={selected}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, chosen) => option.id === chosen.id}
      onChange={(_event, next) => onChange(next.map((option) => option.id))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};
